import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useBlocker, useLocation, useNavigate } from 'react-router';
import { ROI_CALCULATORS, ROI_CALCULATOR_IDS, type RoiProject } from '@wr/shared';
import { errorMessage, post } from '../../api.js';
import { LinkPicker, type LinkItem } from '../../components/LinkPicker.js';
import { Dialog, Field, Notice, SelectField } from '../../components/ui.js';
import { newId } from '../../offline/ids.js';
import { DUPLICATE_SCENARIO, isDuplicateScenario } from './ProjectDialogs.js';

type Draft = { calculator: string; link: LinkItem | null; scenarioName: string };
type Errors = Partial<Record<keyof Draft, string>>;
const EMPTY: Draft = { calculator: '', link: null, scenarioName: '' };

/**
 * New project (brief section 7.7): calculator, Linked to, scenario name; no
 * test project. Linked to is the link picker: a company, a contact or a lead
 * of the portal (HubSpot companies, contacts and deals join at step 4), or a
 * company name as typed when the client is not in the portal yet (brief
 * section 3). Creating a project needs the network.
 */

/** The project's link, from what the picker gives. */
export function roiLinkOf(item: LinkItem) {
  return {
    kind: item.kind,
    label: item.label,
    company: item.company || item.label,
    portalId: item.id,
    hubspotId: item.hubspotId ?? null,
  };
}
export function NewProject() {
  const navigate = useNavigate();
  const location = useLocation();
  const formId = useId();
  // Run ROI from a lead sheet comes with the lead as its link.
  const [initial] = useState<Draft>(() => ({
    ...EMPTY,
    link: (location.state as { link?: LinkItem } | null)?.link ?? null,
  }));
  const [draft, setDraft] = useState<Draft>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const created = useRef(false);

  // Unsaved changes: ask before leaving (Back, menu, closing the tab).
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && !created.current && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (!dirty) return;
    const onLeave = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [dirty]);

  const close = () => navigate({ pathname: '/roi', search: location.search });
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft({ ...draft, [key]: value });
    setErrors({ ...errors, [key]: undefined });
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const scenarioName = draft.scenarioName.trim();
    const missing: Errors = {};
    if (!draft.calculator) missing.calculator = 'Required.';
    if (!draft.link) missing.link = 'Required.';
    if (!scenarioName) missing.scenarioName = 'Required.';
    setErrors(missing);
    if (Object.keys(missing).length > 0) return;
    setBusy(true);
    setFailed(null);
    try {
      const project = await post<RoiProject>('/roi/projects', {
        id: newId(),
        calculator: draft.calculator,
        scenarioName,
        link: roiLinkOf(draft.link!),
      });
      created.current = true;
      // The new project replaces this window in the history.
      navigate(`/roi/${project.id}`, { replace: true });
    } catch (err) {
      if (isDuplicateScenario(err)) setErrors({ scenarioName: DUPLICATE_SCENARIO });
      else setFailed(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Create New Project"
      onClose={close}
      footer={
        <>
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn primary" disabled={busy}>
            Create
          </button>
        </>
      }
    >
      <form id={formId} className="dlg-form" noValidate onSubmit={(e) => void submit(e)}>
        <SelectField
          label="Calculator Type"
          mark
          required
          value={draft.calculator}
          error={errors.calculator}
          onChange={(e) => set('calculator', e.target.value)}
        >
          <option value="">Select calculator...</option>
          {ROI_CALCULATOR_IDS.map((id) => (
            <option key={id} value={id}>
              {ROI_CALCULATORS[id].name}
            </option>
          ))}
        </SelectField>
        <LinkPicker
          label="Linked to"
          mark
          value={draft.link}
          error={errors.link}
          hint="Search the portal"
          allowTyped
          onChange={(link) => set('link', link)}
        />
        <p className="note" style={{ margin: '-8px 0 0' }}>
          A company, a contact or a lead from the portal, or a new company name. The client name
          comes from it.
        </p>
        <Field
          label="Scenario Name"
          mark
          required
          placeholder="Base case"
          value={draft.scenarioName}
          error={errors.scenarioName}
          onChange={(e) => set('scenarioName', e.target.value)}
        />
        {failed && <Notice kind="error">{failed}</Notice>}
      </form>
      {blocker.state === 'blocked' && (
        <Dialog
          small
          title="Close without saving?"
          onClose={() => blocker.reset?.()}
          footer={
            <>
              <button type="button" className="btn" autoFocus onClick={() => blocker.reset?.()}>
                Keep editing
              </button>
              <button type="button" className="btn primary" onClick={() => blocker.proceed?.()}>
                Close without saving
              </button>
            </>
          }
        >
          <p className="note">This project is not created yet.</p>
        </Dialog>
      )}
    </Dialog>
  );
}
